import React, { useMemo, useCallback, useLayoutEffect, useEffect } from "react";
import {
  MemCommonRichTextInputInstance,
  MemCommonRichTextInputInitializer,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputEvent,
  MemCommonRichTextInputEventKind,
  MemCommonRichTextInput,
  MemCommonMentionKeyDownKind,
} from "@mem-labs/common-editor";
import { StyledExpand } from "@/mds/components/forms/combobox/combobox.style";
import { JsonObject } from "type-fest";
import styled from "@emotion/styled";
import { useMemCommonEditorTheme } from "@/mds";

type Props = {
  onMentionQuerySet?: (query: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
  onIsEmptyChange?: (isEmpty: boolean) => void;
  inputRef: React.RefObject<MemCommonRichTextInputInstance>;
  onSubmit?: ({
    mdxContent,
    htmlContent,
    markdownContent,
    tiptapJsonContent,
  }: {
    mdxContent: string;
    htmlContent: string;
    markdownContent: string;
    tiptapJsonContent: JsonObject;
  }) => void;
  onContentModified?: () => void;
  autoFocus?: boolean;
  autoSubmit?: boolean;
  onArrowKeyPressed?: (direction: "up" | "down") => void;
  onEnterPressed?: () => void;
  onInputFocus?: () => void;
  onInputBlur?: () => void;
  onInputChange?: () => void;
  dimensions?: {
    minHeight?: number;
    maxHeight?: number;
    padding?: string;
  };
};

export const CommonEditorInput: React.FC<Props> = ({
  isDisabled = false,
  inputRef,
  placeholder,
  autoFocus = false,
  autoSubmit = true,
  onMentionQuerySet,
  onIsEmptyChange,
  onSubmit,
  onContentModified,
  onArrowKeyPressed,
  onEnterPressed,
  onInputFocus,
  onInputBlur,
  onInputChange,
  dimensions,
}) => {
  const uiTheme = useMemCommonEditorTheme();
  useEffect(() => {
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SetTheme,
      payload: { theme: uiTheme },
    });
  }, [inputRef, uiTheme]);

  const chatInputEventHandler = useCallback(
    (event: MemCommonRichTextInputEvent) => {
      if (
        event.kind !== "focus-changed" &&
        event.kind !== "content-height" &&
        !(event.kind === "selection-changed" && !event.payload) // selection-changed with no payload object fires when the editor is not fully mounted, so we don't trust the contents at this time
      ) {
        // Content has changed, trigger the input change handler
        onInputChange?.();
      }

      switch (event.kind) {
        case MemCommonRichTextInputEventKind.ContentModified: {
          onContentModified?.();
          break;
        }
        case MemCommonRichTextInputEventKind.Contents: {
          onSubmit?.({
            ...event.payload,
          });
          break;
        }
        case MemCommonRichTextInputEventKind.MentionUpdateQuery: {
          const mentionQuery = event.payload.mentionQuery;
          if (!mentionQuery) {
            // Close dropdown if mention char is removed.
            // setMentionClientRect(undefined);
            onMentionQuerySet?.("");
            // setMenuOpenVia(null);
            break;
          }

          const isInsertMenu = mentionQuery[0] === "/";
          if (isInsertMenu) {
            // Slash menu is not meant to be used to insert mentions but to insert content
            // (files, images, templates, etc) or formatting commands.
            // So we're letting common-editor know we're not showing any dropdowns.
            inputRef.current?.dispatchAction({
              kind: MemCommonRichTextInputActionKind.CloseMention,
              payload: null,
            });
            break;
          }

          if (event.payload.clientRect) {
            onMentionQuerySet?.(mentionQuery);
            // setMentionClientRect(event.payload.clientRect ?? undefined);
          }

          inputRef.current?.dispatchAction({
            kind: MemCommonRichTextInputActionKind.FocusEditor,
            payload: null,
          });

          break;
        }
        case MemCommonRichTextInputEventKind.MentionKeyDown: {
          switch (event.payload.kind) {
            case MemCommonMentionKeyDownKind.ArrowDown: {
              onArrowKeyPressed?.("down");
              break;
            }
            case MemCommonMentionKeyDownKind.ArrowUp: {
              onArrowKeyPressed?.("up");
              break;
            }
            case MemCommonMentionKeyDownKind.Enter: {
              onEnterPressed?.();
              break;
            }
          }
          break;
        }
        case MemCommonRichTextInputEventKind.IsEmpty: {
          onIsEmptyChange?.(event.payload.isEmpty);
          break;
        }
        case MemCommonRichTextInputEventKind.FocusChanged: {
          if (event.payload.focus) {
            onInputFocus?.();
          } else {
            // Defer blur handling to allow pending click events on links/buttons to complete
            setTimeout(() => {
              onInputBlur?.();
            }, 500);
          }
          break;
        }
      }
    },
    [
      onSubmit,
      onMentionQuerySet,
      onIsEmptyChange,
      inputRef,
      onArrowKeyPressed,
      onEnterPressed,
      onInputFocus,
      onInputBlur,
      onInputChange,
      onContentModified,
    ]
  );
  const chatInputInitializer: MemCommonRichTextInputInitializer = useMemo(() => {
    return async () => ({
      autoFocus: autoFocus && !isDisabled,
      autoSubmit,
      theme: uiTheme,
      isDisabled: isDisabled,
      clearAfterSendContents: autoSubmit,
    });
  }, [autoFocus, autoSubmit, isDisabled, uiTheme]);

  useLayoutEffect(() => {
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SetReadOnly,
      payload: { readOnly: isDisabled },
    });
  }, [inputRef, isDisabled]);

  return (
    <StyledExpand data-name="expand">
      <StyledRichTextInput
        minHeight={dimensions?.minHeight}
        maxHeight={dimensions?.maxHeight}
        padding={dimensions?.padding}
        richTextInputEventHandler={chatInputEventHandler}
        richTextInputInitializer={chatInputInitializer}
        richTextInputInstanceRef={inputRef}
        placeholder={placeholder}
        // Explicitly set the urlRoot to the Mem domain because we run this in a content script
        urlRoot="https://mem.ai/"
        uiTheme={uiTheme}
      ></StyledRichTextInput>
    </StyledExpand>
  );
};

const StyledRichTextInput = styled(MemCommonRichTextInput)<{
  minHeight?: number;
  maxHeight?: number;
  padding?: number | string;
}>`
  .ProseMirror {
    ${({ minHeight }) => minHeight && `min-height: ${minHeight}px;`}
    ${({ maxHeight }) => maxHeight && `max-height: ${maxHeight}px;`}
    ${({ padding }) => padding && `padding: ${padding};`}
  }
`;
